import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    Check,
    Globe,
    Smartphone,
    Braces,
    CloudCog,
    ShieldCheck,
    Compass,
    ShoppingBag,
    Radar,
    Activity,
    ChevronDown,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, KineticText, Counter, Eyebrow } from "@/components/Reveal";
import TempMailDemo from "@/components/TempMailDemo";
import { blogImages, heroImage, aboutImage } from "@/lib/blogImages";

const serviceIcons = {
    web: Globe,
    mobile: Smartphone,
    api: Braces,
    cloud: CloudCog,
    security: ShieldCheck,
    consulting: Compass,
};

const projectArt = {
    commerce: { icon: ShoppingBag, grad: "from-cobalt/25 via-ink2 to-ink2" },
    fleetops: { icon: Radar, grad: "from-mint/20 via-ink2 to-ink2" },
    medisync: { icon: Activity, grad: "from-neon/20 via-ink2 to-ink2" },
};

const marqueeItems = [
    "React", "Node.js", "TypeScript", "Python", "Go", "PostgreSQL",
    "MongoDB", "Redis", "Kubernetes", "Docker", "AWS", "GraphQL",
];

function CodeCard() {
    return (
        <div className="glass animate-floaty relative z-10 rounded-2xl p-5 font-mono text-[11px] leading-6 sm:text-xs">
            <div className="flex items-center gap-1.5 border-b border-slate-800 pb-3">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-mint/70" />
                <span className="ml-3 text-slate-500">terminal — maxth deploy</span>
            </div>
            <div className="pt-3">
                <p className="text-slate-500">$ maxth deploy --production</p>
                <p className="text-slate-300">
                    ▸ Building application... <span className="text-mint">✓ 1.2s</span>
                </p>
                <p className="text-slate-300">
                    ▸ Test suite (148 specs) <span className="text-mint">✓ passed</span>
                </p>
                <p className="text-slate-300">
                    ▸ Optimizing assets <span className="text-mint">✓ -64% size</span>
                </p>
                <p className="text-slate-300">
                    ▸ Deploying to edge network <span className="text-mint">✓ 12 regions</span>
                </p>
                <p className="pt-2 text-neon">
                    → your-product.live <span className="text-mint">● ONLINE</span>
                </p>
            </div>
        </div>
    );
}

export default function Home() {
    const { t } = useLanguage();
    const h = t.home;

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden pt-36 sm:pt-44">
                <div className="grid-bg absolute inset-0" aria-hidden="true" />
                <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-cobalt/15 blur-[120px]" aria-hidden="true" />
                <div className="absolute -right-32 top-64 h-96 w-96 rounded-full bg-neon/10 blur-[120px]" aria-hidden="true" />

                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid items-center gap-14 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <motion.p
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="mb-6 flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-neon sm:text-xs"
                                data-testid="hero-eyebrow"
                            >
                                <span className="inline-block h-px w-10 bg-neon/60" />
                                {h.eyebrow}
                            </motion.p>
                            <h1 className="font-display text-4xl font-extrabold leading-[1.18] tracking-tight text-white sm:text-5xl lg:text-6xl">
                                <KineticText text={h.heroLine1} delay={0.15} className="block" />
                                <KineticText text={h.heroLine2} delay={0.45} className="block" wordClassName="text-gradient" />
                            </h1>
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 1.0 }}
                                className="mt-7 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base"
                                data-testid="hero-subtitle"
                            >
                                {h.heroSub}
                            </motion.p>
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 1.2 }}
                                className="mt-9 flex flex-wrap items-center gap-4"
                            >
                                <Link
                                    to="/lien-he"
                                    data-testid="hero-cta-primary"
                                    className="group flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-white"
                                >
                                    {h.ctaPrimary}
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/dich-vu"
                                    data-testid="hero-cta-secondary"
                                    className="rounded-full border border-slate-700 px-7 py-3.5 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-neon/60 hover:text-neon"
                                >
                                    {h.ctaSecondary}
                                </Link>
                            </motion.div>
                        </div>

                        <div className="relative lg:col-span-5">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.5 }}
                                className="relative"
                            >
                                <div className="absolute -right-4 -top-10 hidden w-full rotate-3 overflow-hidden rounded-2xl border border-slate-800 opacity-50 sm:block">
                                    <img
                                        src={heroImage}
                                        alt="Hạ tầng máy chủ MAXTH"
                                        className="aspect-[4/3] w-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                                </div>
                                <CodeCard />
                            </motion.div>
                        </div>
                    </div>

                    <Reveal className="mt-24 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-800 bg-slate-800 lg:grid-cols-4">
                        {h.stats.map((s, i) => (
                            <div key={i} className="bg-ink2/80 p-6 sm:p-8" data-testid={`stat-${i}`}>
                                <p className="font-display text-3xl font-bold text-white sm:text-4xl">
                                    <Counter value={s.value} suffix={s.suffix} decimals={s.decimals} />
                                </p>
                                <p className="mt-2 text-xs text-slate-500 sm:text-sm">{s.label}</p>
                            </div>
                        ))}
                    </Reveal>

                    <div className="flex justify-center py-10">
                        <ChevronDown className="h-5 w-5 animate-bounce text-slate-600" aria-label={h.scrollHint} />
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <section className="overflow-hidden border-y border-slate-800/70 py-5" aria-hidden="true">
                <div className="marquee-track flex w-max items-center gap-10">
                    {[...marqueeItems, ...marqueeItems].map((item, i) => (
                        <span key={i} className="flex items-center gap-10 font-mono text-sm text-slate-600">
                            {item}
                            <span className="text-neon/40">//</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                <div className="grid items-center gap-14 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="relative">
                            <div className="overflow-hidden rounded-2xl border border-slate-800">
                                <img
                                    src={aboutImage}
                                    alt="Kỹ sư MAXTH làm việc"
                                    loading="lazy"
                                    className="aspect-[4/5] w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                            </div>
                            <div className="glass absolute -bottom-6 -right-3 rounded-xl px-5 py-4 sm:-right-6">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    MST / Tax code
                                </p>
                                <p className="mt-1 font-mono text-lg font-bold text-neon">0319622735</p>
                            </div>
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>{h.aboutEyebrow}</Eyebrow>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                {h.aboutTitle}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base">{h.aboutText1}</p>
                            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{h.aboutText2}</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                                {h.aboutPoints.map((p, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <Link
                                to="/gioi-thieu"
                                data-testid="home-about-link"
                                className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-neon"
                            >
                                {h.aboutLink}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="border-t border-slate-800/60 bg-ink2/30">
                <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <Reveal>
                            <Eyebrow>{h.servicesEyebrow}</Eyebrow>
                            <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                {h.servicesTitle}
                            </h2>
                            <p className="mt-4 max-w-lg text-sm text-slate-400 sm:text-base">{h.servicesSub}</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link
                                to="/dich-vu"
                                data-testid="home-services-link"
                                className="group flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-200 transition-colors hover:border-neon/60 hover:text-neon"
                            >
                                {h.viewAllServices}
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {t.services.items.map((s, i) => {
                            const Icon = serviceIcons[s.id];
                            return (
                                <Reveal key={s.id} delay={0.06 * i} className={i % 3 === 1 ? "lg:translate-y-8" : ""}>
                                    <div
                                        className="glass card-lift group h-full rounded-2xl p-7"
                                        data-testid={`service-card-${s.id}`}
                                    >
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-700 bg-ink text-neon transition-colors duration-300 group-hover:border-neon/50">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h3 className="mt-6 font-display text-lg font-bold text-white sm:text-xl">
                                            {s.title}
                                        </h3>
                                        <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                        <div className="mt-5 flex flex-wrap gap-2">
                                            {s.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="rounded-full border border-slate-800 bg-ink px-2.5 py-1 font-mono text-[10px] text-slate-500"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FEATURED PRODUCT: TEMP MAIL */}
            <section className="relative overflow-hidden">
                <div className="absolute left-1/2 top-0 h-80 w-[42rem] -translate-x-1/2 rounded-full bg-neon/5 blur-[120px]" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                    <div className="grid items-center gap-14 lg:grid-cols-2">
                        <div>
                            <Reveal>
                                <Eyebrow>{h.featuredEyebrow}</Eyebrow>
                                <h2 className="flex flex-wrap items-center gap-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                    Temp Mail
                                    <span className="flex items-center gap-1.5 rounded-full border border-mint/30 bg-mint/10 px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-widest text-mint">
                                        <span className="status-dot h-1.5 w-1.5 rounded-full bg-mint" />
                                        {h.featuredBadge}
                                    </span>
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base">
                                    {h.featuredText}
                                </p>
                                <ul className="mt-7 space-y-3">
                                    {h.featuredPoints.map((p, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                            <Reveal delay={0.18}>
                                <a
                                    href="https://maxthsolutions.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="tempmail-visit-link"
                                    className="group mt-9 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white"
                                >
                                    {h.visitSite}
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </Reveal>
                        </div>
                        <Reveal delay={0.12}>
                            <TempMailDemo />
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* OTHER PROJECTS */}
            <section className="border-t border-slate-800/60 bg-ink2/30">
                <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <Reveal>
                            <Eyebrow>{h.projectsEyebrow}</Eyebrow>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                {h.projectsTitle}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link
                                to="/du-an"
                                data-testid="home-projects-link"
                                className="group flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-200 transition-colors hover:border-neon/60 hover:text-neon"
                            >
                                {h.viewAllProjects}
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-14 grid gap-5 md:grid-cols-3">
                        {t.projects.items
                            .filter((p) => p.id !== "tempmail")
                            .map((p, i) => {
                                const art = projectArt[p.id];
                                const Icon = art.icon;
                                return (
                                    <Reveal key={p.id} delay={0.08 * i}>
                                        <Link
                                            to="/du-an"
                                            data-testid={`home-project-${p.id}`}
                                            className="glass card-lift group block h-full overflow-hidden rounded-2xl"
                                        >
                                            <div className={`grid-bg relative flex h-40 items-center justify-center bg-gradient-to-br ${art.grad}`}>
                                                <Icon className="h-10 w-10 text-slate-500 transition-colors duration-500 group-hover:text-neon" />
                                                <span className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                                                    {p.status}
                                                </span>
                                            </div>
                                            <div className="p-6">
                                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                    {p.category}
                                                </p>
                                                <h3 className="mt-2 font-display text-lg font-bold text-white">
                                                    {p.name}
                                                </h3>
                                                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-400">
                                                    {p.desc}
                                                </p>
                                            </div>
                                        </Link>
                                    </Reveal>
                                );
                            })}
                    </div>
                </div>
            </section>

            {/* BLOG TEASER */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <Reveal>
                        <Eyebrow>{h.blogEyebrow}</Eyebrow>
                        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                            {h.blogTitle}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/blog"
                            data-testid="home-blog-link"
                            className="group flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-200 transition-colors hover:border-neon/60 hover:text-neon"
                        >
                            {h.viewAllBlog}
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-5 md:grid-cols-3">
                    {t.blog.posts.slice(0, 3).map((post, i) => (
                        <Reveal key={post.slug} delay={0.08 * i}>
                            <Link
                                to={`/blog/${post.slug}`}
                                data-testid={`home-blog-card-${post.slug}`}
                                className="glass card-lift group block h-full overflow-hidden rounded-2xl"
                            >
                                <div className="relative overflow-hidden">
                                    <img
                                        src={blogImages[post.slug]}
                                        alt={post.title}
                                        loading="lazy"
                                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span className="absolute left-4 top-4 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-neon backdrop-blur">
                                        {post.category}
                                    </span>
                                </div>
                                <div className="p-6">
                                    <p className="font-mono text-[10px] text-slate-500">
                                        {post.date} · {post.readTime}
                                    </p>
                                    <h3 className="mt-3 font-display text-base font-bold leading-snug text-white sm:text-lg">
                                        {post.title}
                                    </h3>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-neon">
                                        {h.readMore}
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
                <Reveal>
                    <div className="glass relative overflow-hidden rounded-3xl px-8 py-16 text-center sm:px-16 sm:py-20">
                        <div className="grid-bg absolute inset-0 opacity-60" aria-hidden="true" />
                        <div className="absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-neon/10 blur-[100px]" aria-hidden="true" />
                        <div className="relative">
                            <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                {h.ctaTitle}
                            </h2>
                            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                                {h.ctaText}
                            </p>
                            <Link
                                to="/lien-he"
                                data-testid="home-cta-button"
                                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-semibold text-ink transition-colors hover:bg-white"
                            >
                                {h.ctaButton}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
